import type { GetMembersByWorkspaceIdQuery } from '@affine/graphql';
import {
  catchErrorInto,
  effect,
  Entity,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  smartRetry,
} from '@toeverything/infra';
import { map, switchMap, tap } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type { WorkspaceMembersStore } from '../stores/members';
import type { TeamNode } from './teams';

export type Member =
  GetMembersByWorkspaceIdQuery['workspace']['members'][number];

export class WorkspaceMembers extends Entity {
  constructor(
    private readonly store: WorkspaceMembersStore,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
  }

  pageNum$ = new LiveData(0);
  team$ = new LiveData<TeamNode | undefined>(undefined);
  memberCount$ = new LiveData<number | undefined>(undefined);
  teamMemberCount$ = new LiveData<number | undefined>(undefined);
  pageMembers$ = new LiveData<Member[] | undefined>(undefined);

  isLoading$ = new LiveData(false);
  error$ = new LiveData<any>(null);

  readonly PAGE_SIZE = 8;

  readonly revalidate = effect(
    map(() => ({
      pageNum: this.pageNum$.value,
      teamId: this.team$.value?.id,
    })),
    switchMap(({ pageNum, teamId }) => {
      return fromPromise(async signal => {
        return this.store.fetchMembers(
          this.workspaceService.workspace.id,
          pageNum * this.PAGE_SIZE,
          this.PAGE_SIZE,
          teamId,
          signal
        );
      }).pipe(
        tap(data => {
          this.teamMemberCount$.setValue(data.teamMemberCount);
          this.memberCount$.setValue(data.memberCount);
          this.pageMembers$.setValue(data.members);
        }),
        smartRetry(),
        catchErrorInto(this.error$),
        onStart(() => {
          this.pageMembers$.setValue(undefined);
          this.isLoading$.setValue(true);
        }),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  setPageNum(pageNum: number) {
    this.pageNum$.setValue(pageNum);
  }

  setTeamId(team: TeamNode | undefined) {
    this.team$.setValue(team);
    this.pageNum$.setValue(0);
  }

  override dispose(): void {
    this.revalidate.unsubscribe();
  }
}
